import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Calendar, FileArchive, AlertCircle, CheckCircle } from 'lucide-react';
import type { ExportResponse } from '../types/api';

export default function ExportPage() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const exportMutation = useMutation<ExportResponse | Blob, Error, { format: 'csv' | 'zip'; start_date?: string; end_date?: string; status?: string; country?: string; }>({
    mutationFn: api.exportInvoices,
    onSuccess: (response) => {
      // Handle export response - check if it's the new API format or legacy blob
      if ('download_url' in response && response.download_url) {
        // New API format with download URL
        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = response.filename || 'export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback: treat as blob for mock API compatibility
        const blob = response as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename with date range
        const filename = `invoices_${startDate}_to_${endDate}.zip`;
        link.download = filename;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup
        window.URL.revokeObjectURL(url);
      }
      
      setDownloadStatus('success');
      
      // Reset status after 3 seconds
      setTimeout(() => setDownloadStatus('idle'), 3000);
    },
    onError: (error) => {
      console.error('Export failed:', error);
      setDownloadStatus('error');
      
      // Reset status after 5 seconds
      setTimeout(() => setDownloadStatus('idle'), 5000);
    },
  });

  const handleExport = () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('Start date must be before end date');
      return;
    }

    exportMutation.mutate({
      format: 'zip', // Default format
      start_date: startDate,
      end_date: endDate,
    });
  };

  const getStatusMessage = () => {
    switch (downloadStatus) {
      case 'success':
        return {
          icon: <CheckCircle className="h-5 w-5 text-emerald-500" />,
          message: 'Export completed successfully! Your download should start automatically.',
          className: 'bg-emerald-50 border-green-200 text-green-800'
        };
      case 'error':
        return {
          icon: <AlertCircle className="h-5 w-5 text-destructive" />,
          message: 'Export failed. Please try again or contact support if the problem persists.',
          className: 'bg-destructive/10 border-destructive/20 text-destructive'
        };
      default:
        return null;
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Export Invoices</h1>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileArchive className="h-5 w-5 mr-2" />
              Bulk Export
            </CardTitle>
            <CardDescription>
              Export invoices in ZIP format containing JSON and signed XML files for a specified date range.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date Range Selection */}            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Start Date
                </label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label htmlFor="end-date" className="block text-sm font-medium mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  End Date
                </label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {/* Export Button */}
            <div className="flex justify-center">
              <Button
                onClick={handleExport}
                disabled={!startDate || !endDate || exportMutation.isPending}
                size="lg"
                className="w-full md:w-auto"
              >
                {exportMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating ZIP...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Generate ZIP
                  </>
                )}
              </Button>
            </div>

            {/* Status Message */}
            {statusMessage && (
              <div className={`p-4 rounded-lg border flex items-start space-x-3 ${statusMessage.className}`}>
                {statusMessage.icon}
                <p className="text-sm">{statusMessage.message}</p>
              </div>
            )}

            {/* Export Information */}
            <div className="bg-primary/10 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Export Information</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• The ZIP file will contain both JSON and signed XML versions of all invoices</li>
                <li>• Large date ranges may take longer to process</li>
                <li>• Files are organized by country and date for easy navigation</li>
                <li>• Maximum export range is 90 days per request</li>
              </ul>
            </div>

            {/* Quick Date Presets */}
            <div>
              <h4 className="font-medium text-foreground mb-3">Quick Presets</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const todayStr = today.toISOString().split('T')[0];
                    setStartDate(todayStr);
                    setEndDate(todayStr);
                  }}
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = yesterday.toISOString().split('T')[0];
                    setStartDate(yesterdayStr);
                    setEndDate(yesterdayStr);
                  }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    setStartDate(weekAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    const monthAgo = new Date(today);
                    monthAgo.setDate(monthAgo.getDate() - 30);
                    setStartDate(monthAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                >
                  Last 30 Days
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
